import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

function FutureDatePicker({ selectedDate, onDateChange }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  function formatDate(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  function handleDateSelect(day) {
    if (!day || !canSelectDate(day)) return;

    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    onDateChange(`${year}-${month}-${dayPadded}`);
    setShowCalendar(false);
  }

  function canSelectDate(day) {
    if (!day) return false;

    const now = new Date();
    const currentHour = now.getHours();
    const selectedDate = new Date(currentYear, currentMonth, day);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If date is in the past, cannot select
    if (selectedDate < today) {
      return false;
    }

    // If date is today and it's past 1pm (13:00), cannot select
    if (selectedDate.getTime() === today.getTime() && currentHour >= 13) {
      return false;
    }

    // Otherwise can select (today before 1pm, or future dates)
    return true;
  }

  function isSelectedDate(day) {
    if (!day || !selectedDate) return false;
    const [year, month, dayOfMonth] = selectedDate.split('-').map(Number);
    return (
      dayOfMonth === day &&
      (month - 1) === currentMonth &&
      year === currentYear
    );
  }

  function goToPreviousMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }

  function goToNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
        Fecha de Entrega
      </label>
      
      <button
        type="button"
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all flex items-center justify-between font-montserrat hover:border-[#B1336E]"
      >
        <span className={selectedDate ? 'text-slate-800 font-medium' : 'text-slate-400'}>
          {selectedDate ? formatDate(selectedDate) : 'Selecciona una fecha'}
        </span>
        <Calendar className="w-4 h-4 text-slate-400" />
      </button>

      {showCalendar && (
        <div className="absolute top-full left-0 mt-2 w-full max-w-xs bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30">
          {/* Month/Year Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-slate-100 rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            
            <div className="text-center">
              <p className="text-xs font-bold text-slate-800 font-montserrat">
                {monthNames[currentMonth]} {currentYear}
              </p>
            </div>
            
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-1 hover:bg-slate-100 rounded transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayNames.map((day) => (
              <div key={day} className="text-center text-[10px] font-semibold text-slate-500 py-1 font-montserrat">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarDays.map((day, index) => {
              const canSelect = canSelectDate(day);
              const isSelected = isSelectedDate(day);
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  disabled={!day || !canSelect}
                  className={`
                    aspect-square rounded text-xs transition-all font-montserrat
                    ${!day ? 'invisible' : ''}
                    ${!canSelect ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-[#B1336E]/10'}
                    ${isSelected ? 'bg-[#B1336E] text-white font-bold hover:bg-[#B1336E]/90' : 'text-slate-700'}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Info Message */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-[10px] text-slate-500 font-montserrat text-center">
              {new Date().getHours() >= 13 
                ? 'Solo fechas futuras (después de hoy 1pm)'
                : 'Fechas desde hoy (antes de 1pm)'
              }
            </p>
          </div>

          {/* Footer */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowCalendar(false)}
              className="w-full py-1.5 text-xs text-slate-600 hover:text-slate-800 transition-colors font-montserrat"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FutureDatePicker;